/**
 * Tokenise a plain-text message into segments: literal text, plain URLs, and
 * recognised internal-app routes (which the renderer will draw as compact chips).
 *
 * Kept in a pure function so it's trivially unit-testable and side-effect free.
 */

export type AppLinkKind =
  | "asset"
  | "profile"
  | "customer"
  | "document"
  | "onboarding"
  | "business_trip"
  | "leave"
  | "unknown";

export type ChatContentPart =
  | { type: "text"; value: string }
  | { type: "url"; url: string }
  | { type: "app_link"; url: string; route: string; kind: AppLinkKind };

// Reasonably safe URL matcher: scheme://… until whitespace or a small set of
// trailing punctuation commonly attached by mistake (.,!?)).
const URL_REGEX = /(https?:\/\/[^\s)<>"]+)/g;

interface AppLinkPattern {
  segment: RegExp;
  kind: AppLinkKind;
}

const APP_LINK_PATTERNS: AppLinkPattern[] = [
  { segment: /^\/app\/[^/]+\/assets\/[^/]+/, kind: "asset" },
  { segment: /^\/app\/[^/]+\/profile\/[^/]+/, kind: "profile" },
  { segment: /^\/app\/[^/]+\/customers\/[^/]+/, kind: "customer" },
  { segment: /^\/app\/[^/]+\/documents\/view\/[^/]+/, kind: "document" },
  { segment: /^\/app\/[^/]+\/onboarding\/[^/]+/, kind: "onboarding" },
  { segment: /^\/app\/[^/]+\/business-trips?\/[^/]+/, kind: "business_trip" },
  { segment: /^\/app\/[^/]+\/leave-calendar/, kind: "leave" },
  { segment: /^\/app\/[^/]+\/leaves?\//, kind: "leave" },
];

/** Strip trailing punctuation that's almost always not part of the URL. */
function trimTrailingPunct(url: string): { core: string; trail: string } {
  const m = url.match(/^(.*?)([.,!?;:]+)$/);
  return m ? { core: m[1], trail: m[2] } : { core: url, trail: "" };
}

function classifyAppLink(
  url: string,
  origin: string
): { route: string; kind: AppLinkKind } | null {
  try {
    const u = new URL(url);
    if (u.origin !== origin) return null;
    const pathAndSearch = u.pathname + u.search;
    for (const { segment, kind } of APP_LINK_PATTERNS) {
      if (segment.test(u.pathname)) {
        return { route: pathAndSearch, kind };
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function parseMessageContent(
  content: string,
  origin: string
): ChatContentPart[] {
  const out: ChatContentPart[] = [];
  let lastIndex = 0;
  // Reset lastIndex on the global regex by using exec in a loop (avoids
  // requiring downlevelIteration for matchAll).
  const re = new RegExp(URL_REGEX.source, URL_REGEX.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(content)) !== null) {
    const start = m.index;
    if (start > lastIndex) {
      out.push({ type: "text", value: content.slice(lastIndex, start) });
    }
    const { core, trail } = trimTrailingPunct(m[0]);
    const appLink = classifyAppLink(core, origin);
    if (appLink) {
      out.push({ type: "app_link", url: core, ...appLink });
    } else {
      out.push({ type: "url", url: core });
    }
    if (trail) out.push({ type: "text", value: trail });
    lastIndex = start + m[0].length;
  }
  if (lastIndex < content.length) {
    out.push({ type: "text", value: content.slice(lastIndex) });
  }
  return out;
}
